import { nextSegmentIndex, coverageSublabelText } from "../lib/regionSwitchLogic";
import type { RegionId } from "../lib/sources";

export interface RegionSwitchOptions {
  initialRegion: RegionId;
  /** Called on activation (click, or Enter/Space on the focused segment) — the caller owns the actual switchRegion() work. */
  onSelect: (region: RegionId) => void;
}

interface Segment {
  region: RegionId;
  button: HTMLButtonElement;
}

/**
 * "USA | EUROPA" segmented region switch (DESIGN.md Aneks M003 §R1). A
 * deliberately NON-standard radiogroup keyboard pattern: arrow keys move
 * focus WITHOUT activating (activation costs a network round-trip/region
 * load — noted in DECISIONS.md), Enter/Space activates the focused segment.
 * Persistence (localStorage["radar.region"]) is handled by app.ts's
 * switchRegion(), not here.
 */
export class RegionSwitch {
  readonly element: HTMLElement;
  private readonly group: HTMLDivElement;
  private readonly sublabelEl: HTMLSpanElement;
  private readonly segments: Segment[];
  private activeRegion: RegionId;
  private readonly opts: RegionSwitchOptions;
  private readonly compactMQ: MediaQueryList | null =
    typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 360px)") : null;

  constructor(opts: RegionSwitchOptions) {
    this.opts = opts;
    this.activeRegion = opts.initialRegion;

    this.element = document.createElement("div");
    this.element.className = "region-switch-wrap";

    this.group = document.createElement("div");
    this.group.className = "region-switch card";
    this.group.setAttribute("role", "radiogroup");
    this.group.setAttribute("aria-label", "Region radaru");
    this.group.addEventListener("keydown", (e) => this.handleKeydown(e));

    this.segments = [
      { region: "us", button: this.createSegment("USA", "us") },
      { region: "eu-de", button: this.createSegment("EUROPA", "eu-de") }
    ];
    for (const seg of this.segments) this.group.appendChild(seg.button);

    this.sublabelEl = document.createElement("span");
    this.sublabelEl.className = "region-switch__sublabel";
    this.sublabelEl.setAttribute("aria-hidden", "true");

    this.element.append(this.group, this.sublabelEl);

    this.compactMQ?.addEventListener?.("change", () => this.render());
    this.render();
  }

  private createSegment(label: string, region: RegionId): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "region-switch__segment";
    btn.setAttribute("role", "radio");
    btn.textContent = label;
    btn.addEventListener("click", () => this.activate(region));
    return btn;
  }

  private isCompact(): boolean {
    return this.compactMQ?.matches ?? false;
  }

  private handleKeydown(e: KeyboardEvent): void {
    const focusedIdx = this.segments.findIndex((s) => s.button === document.activeElement);
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown": {
        e.preventDefault();
        const from = focusedIdx >= 0 ? focusedIdx : this.activeIndex();
        this.segments[nextSegmentIndex(from, 1, this.segments.length)].button.focus();
        break;
      }
      case "ArrowLeft":
      case "ArrowUp": {
        e.preventDefault();
        const from = focusedIdx >= 0 ? focusedIdx : this.activeIndex();
        this.segments[nextSegmentIndex(from, -1, this.segments.length)].button.focus();
        break;
      }
      case "Enter":
      case " ":
      case "Spacebar": {
        e.preventDefault();
        if (focusedIdx >= 0) this.activate(this.segments[focusedIdx].region);
        break;
      }
      default:
        break;
    }
  }

  private activeIndex(): number {
    const idx = this.segments.findIndex((s) => s.region === this.activeRegion);
    return idx >= 0 ? idx : 0;
  }

  private activate(region: RegionId): void {
    if (region === this.activeRegion) return;
    // §R2 step 1: selection updates (and the pill reflects it) immediately —
    // "przełącznik pozostaje aktywny cały czas" — the actual data load is
    // async and handled entirely by the caller's switchRegion().
    this.activeRegion = region;
    this.render();
    this.opts.onSelect(region);
  }

  private render(): void {
    for (const seg of this.segments) {
      const checked = seg.region === this.activeRegion;
      seg.button.setAttribute("aria-checked", String(checked));
      seg.button.tabIndex = checked ? 0 : -1;
      seg.button.classList.toggle("is-active", checked);
    }
    const text = coverageSublabelText(this.activeRegion, this.isCompact());
    this.sublabelEl.hidden = text === null;
    this.sublabelEl.textContent = text ?? "";
  }
}
